import { type ReactNode } from "react";
import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { SplitRow } from "@freightsimple/ui-core/PageLayout/SplitRow";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tab } from "@freightsimple/ui-core/PageLayout/Tab";
import { type ShipmentDetail } from "@freightsimple/generated-carriers-openapi-client/models/ShipmentDetail";
import { formatMoney } from "@/App/Views/carrierFormatting";

/** References, addresses, cargo and pay — what the shipment is. */
export function ShipmentKeyInfoTab({ shipment }: { shipment: ShipmentDetail }) {
  return (
    <Tab id="key-info" title="Key Info">
      <Stack gap={4}>
        <Panel title="References">
          <Grid columns={2}>
            <DetailRow_ label="PRO Number" value={shipment.proNumber} />
            <DetailRow_
              label="BOL Number"
              value={shipment.billOfLadingNumber}
            />
            <DetailRow_
              label="Pickup Confirmation"
              value={shipment.carrierPickupNumber}
            />
            <DetailRow_
              label="Service"
              value={shipment.carrierServiceName ?? shipment.serviceIdentifier}
            />
          </Grid>
        </Panel>

        <Grid columns={2}>
          <Panel title="Pickup">
            <Stack gap={2}>
              <DetailRow_
                label="Business"
                value={shipment.pickupBusinessName}
              />
              <DetailRow_ label="Location" value={shipment.pickup} />
              <DetailRow_ label="Date" value={shipment.pickupDate} />
            </Stack>
          </Panel>
          <Panel title="Delivery">
            <Stack gap={2}>
              <DetailRow_
                label="Business"
                value={shipment.deliveryBusinessName}
              />
              <DetailRow_ label="Location" value={shipment.delivery} />
              <DetailRow_ label="Delivered" value={shipment.deliveryDate} />
            </Stack>
          </Panel>
        </Grid>

        <Panel title="Cargo">
          <Grid columns={2}>
            <DetailRow_ label="Commodity" value={shipment.commodity} />
            <DetailRow_
              label="Handling Units"
              value={shipment.handlingUnits?.toLocaleString()}
            />
            <DetailRow_
              label="Weight"
              value={
                shipment.weightLbs === undefined
                  ? undefined
                  : `${shipment.weightLbs.toLocaleString()} lbs`
              }
            />
            <DetailRow_ label="Freight Class" value={shipment.freightClass} />
          </Grid>
        </Panel>

        <Panel title="Transit & Pay">
          <Grid columns={2}>
            <DetailRow_
              label="Quoted Transit"
              value={
                shipment.quotedTransitDays === undefined
                  ? undefined
                  : `${shipment.quotedTransitDays} days`
              }
            />
            <DetailRow_
              label="Actual Transit"
              value={
                shipment.actualTransitDays === undefined
                  ? undefined
                  : `${shipment.actualTransitDays} days`
              }
            />
            <DetailRow_
              label="Paid"
              value={
                shipment.paidCad === undefined
                  ? undefined
                  : formatMoney(shipment.paidCad)
              }
            />
            <DetailRow_ label="Booked" value={shipment.bookedAt} />
          </Grid>
        </Panel>
      </Stack>
    </Tab>
  );
}

function DetailRow_({ label, value }: { label: string; value: ReactNode }) {
  return (
    <SplitRow
      left={
        <T size="sm" color="secondary">
          {label}
        </T>
      }
      right={<T size="sm">{value ?? "—"}</T>}
    />
  );
}
